import React from "react";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { hasAccess, FEATURE_REQUIREMENTS, PLAN_LABEL, PLAN_PRICE } from "@/lib/plans";

/**
 * Wraps a page/section. If the current user does not have access to `feature`,
 * renders an upsell screen instead of the children.
 */
export default function PlanGate({ feature, children, soft = false }) {
  const { user } = useAuth();
  if (hasAccess(user, feature)) return children;

  const required = FEATURE_REQUIREMENTS[feature] || "growth";
  const price = PLAN_PRICE[required];
  const label = PLAN_LABEL[required];

  if (soft) {
    return (
      <div className="relative">
        <div className="opacity-25 pointer-events-none select-none">{children}</div>
        <UpsellOverlay feature={feature} required={required} label={label} price={price} />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-10 max-w-2xl mx-auto mt-10">
      <UpsellCard feature={feature} required={required} label={label} price={price} />
    </div>
  );
}

function UpsellCard({ feature, required, label, price }) {
  return (
    <div className="text-center" data-testid={`plan-gate-${feature}`}>
      <div className="w-14 h-14 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center mx-auto mb-5">
        <Lock className="w-6 h-6 text-primary" />
      </div>
      <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Bloqueado</p>
      <h2 className="font-display text-3xl font-bold tracking-tight">
        Disponível no plano <span className="text-primary">{label}</span>
      </h2>
      <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
        Esta funcionalidade exige o plano {label} ou superior.
        Faça upgrade e tenha acesso completo a partir de R$ {price.toLocaleString("pt-BR")}/mês.
      </p>
      <div className="flex gap-3 justify-center mt-7">
        <Link to="/app/pricing">
          <Button data-testid={`upgrade-btn-${feature}`} className="bg-primary hover:bg-primary/90 h-11 px-6">
            <Sparkles className="w-4 h-4 mr-2" /> Fazer upgrade
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function UpsellOverlay({ feature, required, label, price }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center backdrop-blur-md bg-background/40">
      <div className="rounded-2xl border border-primary/40 bg-card p-8 max-w-md shadow-2xl">
        <UpsellCard feature={feature} required={required} label={label} price={price} />
      </div>
    </div>
  );
}
